import { resetButtonLoading, showButtonLoading } from "../ui/button-loading";
import { BookingFormValidator } from "../events/booking-form-validator";
export interface PaymentOptions {
    formId: string;
    keyId?: string;
    contactId?: string;
    applicationId?: string;
    successUrl: string;
    failureUrl: string;
    total: string | number;
    item: string;
    invoiceGuid?: string;
}

export interface StripeResponse {
    clientSecret: string;
    publicApiKey: string;
    amount: number;
}

declare global {
    const Stripe: any;
    interface Window {
        initializeEventBookingPayment: Function;
        initializeCourseBookingPayment: Function;
        initializeMembershipApplicationPayment: Function;
        initializeECommercePayment: Function;
    }
}

/**
 * Stripe's card fields dressed as LMA's own form controls (_form.scss), as there's no design for
 * them: DM Sans, 16px labels 8px above a 48px input with a 1px LMA Navy border and 4px corners,
 * the navy focus ring the site's inputs use, and the danger red for errors.
 */
const LMA_NAVY = "#02163A";
const LMA_DANGER = "#dc3545";
const LMA_FOCUS_RING = "0 0 0 0.25rem rgba(2, 22, 58, 0.25)";

const LMA_STRIPE_FONTS = [
    { cssSrc: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap" }
];

const LMA_STRIPE_APPEARANCE = {
    theme: "stripe",
    variables: {
        fontFamily: "'DM Sans', sans-serif",
        fontSizeBase: "16px",
        fontLineHeight: "1.4",
        colorPrimary: LMA_NAVY,
        colorText: LMA_NAVY,
        colorTextSecondary: LMA_NAVY,
        colorBackground: "#FFFFFF",
        colorDanger: LMA_DANGER,
        borderRadius: "4px",
        spacingUnit: "4px",
        gridRowSpacing: "24px",
        gridColumnSpacing: "24px",
        focusBoxShadow: LMA_FOCUS_RING,
        focusOutline: "none"
    },
    rules: {
        ".Label": { color: LMA_NAVY, fontSize: "16px", fontWeight: "400", lineHeight: "1.4", marginBottom: "8px" },
        ".Input": { padding: "12px", border: `1px solid ${LMA_NAVY}`, boxShadow: "none", lineHeight: "1.4" },
        ".Input:focus": { borderColor: LMA_NAVY, boxShadow: LMA_FOCUS_RING },
        ".Input--invalid": { borderColor: LMA_DANGER, boxShadow: "none", color: LMA_NAVY },
        ".Error": { marginTop: "8px", fontSize: "14px", lineHeight: "1.4" },
        ".Tab": { border: `1px solid ${LMA_NAVY}`, boxShadow: "none" },
        ".Tab:focus": { boxShadow: LMA_FOCUS_RING },
        ".Tab--selected, .Tab--selected:hover, .Tab--selected:focus": { borderColor: LMA_NAVY, boxShadow: `0 0 0 1px ${LMA_NAVY}` }
    }
};

const CARD_UNAVAILABLE_MESSAGE =
    "Card payment isn't available at the moment. Please refresh the page to try again, " +
    "or contact registration.lmaevents@lma.eu.com to book.";

export class PaymentService {
    private stripe: any;
    private elements: any;
    private options: PaymentOptions | null = null;

    public async initializeECommercePayment(
        formId: string,
        contactId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string,
        invoiceGuid?: string
    ): Promise<void> {
        this.options = { formId, keyId: contactId, successUrl, failureUrl, total, item, invoiceGuid };
        document.querySelector("#payment-form")
            ?.addEventListener("submit", this.handleSubmitECommerce.bind(this));

        const voucherCode = (document.getElementById("DiscountCode") as HTMLInputElement | null)?.value?.trim() || undefined;
        await this.mountStripeElement("Shop Checkout", total, item, "", voucherCode, invoiceGuid);
        await this.checkStatus();
    }

    private async handleSubmitECommerce(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!keyId || !successUrl || !failureUrl) return;

        await this.submitECommerceWithStripe(formId, keyId, successUrl, failureUrl);
    }

    private async submitECommerceWithStripe(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId, "AgreeToTermsAndConditions");
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            `/umbraco/surface/StripeSurface/SaveEcommerceFormToSession?key=${keyId}`
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/ECommerceSurface/ConfirmAndComplete", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        await this.confirmStripePayment(url.toString());
    }


    public async initializeEventBookingPayment(
        formId: string,
        eventId: string,
        eventBookingId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        this.options = { formId, keyId: eventId, successUrl, failureUrl, total, item };
        // The card fields sit inside the checkout's own form (BookingStripeJsForm.cshtml), so the
        // Pay button is a plain button rather than a nested form's submit.
        document.querySelector("#submit")
            ?.addEventListener("click", this.handleSubmitEventBooking.bind(this));
        await this.mountStripeElement("Event Booking", total, item, eventBookingId);
        await this.checkStatus();
    }

    public async initializeCourseBookingPayment(
        formId: string,
        courseId: string,
        courseBookingId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        this.options = { formId, keyId: courseId, successUrl, failureUrl, total, item };
        document.querySelector("#submit")
            ?.addEventListener("click", this.handleSubmitCourseBooking.bind(this));
        await this.mountStripeElement("Course Booking", total, item, courseBookingId);
        await this.checkStatus();
    }

    private async mountStripeElement(
        userJourney: string,
        total: string | number,
        item: string,
        bookingId: string = "",
        voucherCode?: string,
        invoiceGuid?: string
    ): Promise<void> {
        const isCourse = userJourney === "Course Booking";
        const bookingIdParam = bookingId
            ? (isCourse
                ? `&courseBookingId=${encodeURIComponent(bookingId)}`
                : `&eventBookingId=${encodeURIComponent(bookingId)}`)
            : "";

        const voucherParam = voucherCode ? `&voucherCodeId=${encodeURIComponent(voucherCode)}` : "";
        const invoiceParam = invoiceGuid ? `&invoiceGuid=${encodeURIComponent(invoiceGuid)}` : "";

        // GetPaymentIntent answers with no body when Memex api/pay fails or Stripe isn't configured
        // there, so the card fields can't be shown: say so and switch Pay off, rather than leave a
        // Pay button that saves the form then fails with its spinner still going.
        let intent: StripeResponse | null = null;
        let rateLimited = false;
        try {
            const response = await fetch(
                `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${total}` +
                `&itemName=${encodeURIComponent(item)}` +
                `&userJourney=${encodeURIComponent(userJourney)}` +
                bookingIdParam +
                voucherParam +
                invoiceParam,
                { method: "POST" }
            );
            // Rate limited (ServiceCollectionExtensions.AddRateLimits), with no JSON body.
            rateLimited = response.status === 429;
            if (response.ok && response.status !== 204) intent = await response.json();
        } catch (err) {
            console.error("[payment] GetPaymentIntent failed", err);
        }

        if (!intent?.clientSecret || !intent.publicApiKey) {
            this.stopCardPayment(rateLimited
                ? "Too many attempts. Please wait a minute, then refresh the page to pay by card."
                : CARD_UNAVAILABLE_MESSAGE);
            return;
        }

        const { clientSecret, publicApiKey } = intent;

        this.stripe = Stripe(publicApiKey);
        this.elements = this.stripe.elements({ appearance: LMA_STRIPE_APPEARANCE, fonts: LMA_STRIPE_FONTS, clientSecret });
        this.elements.create("payment", { layout: "tabs" }).mount("#payment-element");
    }

    /**
     * Core invoice submission shared by event and course paths.
     * Callers supply explicit endpoints rather than a boolean flag.
     */
    private async submitInvoiceCore(
        formId: string,
        keyId: string,
        saveEndpoint: string | undefined,
        crmUrl: string,
        successUrl: string,
        failureUrl: string,
        poNumber: string,
        button?: HTMLButtonElement
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);

        if (!BookingFormValidator.validateBookingForm(formId)) return;

        showButtonLoading(button);

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            saveEndpoint
        );
        if (!saved) {
            resetButtonLoading(button);
            return;
        }

        const url = new URL(crmUrl, window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "invoice");
        url.searchParams.set("poNumber", poNumber);
        window.location.href = url.toString();
    }

    /**
     * Validates and clears the PO Number input.
     * Returns the trimmed value on success, or null if validation failed.
     */
    private validatePoNumber(): string | null {
        const poInput = document.getElementById("PoNumber") as HTMLInputElement | null;
        const poError = document.getElementById("PoNumberError") as HTMLElement | null;
        const poNumber = (poInput?.value ?? "").trim();

        if (!poNumber) {
            if (poInput) { poInput.classList.add("input-validation-error"); poInput.focus(); }
            if (poError) { poError.textContent = "Please enter a PO number."; poError.classList.remove("d-none"); }
            return null;
        }

        if (poInput) poInput.classList.remove("input-validation-error");
        if (poError) { poError.textContent = ""; poError.classList.add("d-none"); }
        return poNumber;
    }

    public async submitEventInvoiceFromButton(btn: HTMLElement): Promise<void> {
        // Every field's message at once: the PO number is checked apart from jQuery Validate, so
        // both run before either stops the order.
        const poNumber = this.validatePoNumber();
        const formValid = BookingFormValidator.validateBookingForm("EventBookingForm");
        if (!poNumber || !formValid) return;

        // Posted as the form itself (PlaceOrder, with its anti-forgery token), as a free order
        // is: the PO number is one of its fields.
        const form = document.getElementById("EventBookingForm") as HTMLFormElement | null;
        if (!form) return;

        // Checkout starts on card when it's offered (PaymentMethodType.Card = 0), so the invoice
        // tab's order says it's an invoice (PaymentMethodType.Invoice = 1).
        const method = form.querySelector<HTMLInputElement>("input[name='PaymentMethod']");
        if (method) method.value = "1";

        showButtonLoading(btn as HTMLButtonElement);
        // Not form.submit(): the card tab's Pay button has id "submit", which shadows it.
        HTMLFormElement.prototype.submit.call(form);
    }

    public async submitCourseInvoiceFromButton(btn: HTMLElement): Promise<void> {
        const poNumber = this.validatePoNumber();
        if (!poNumber) return;

        const courseId = btn.getAttribute("data-course-id") ?? "";

        await this.submitInvoiceCore(
            "CourseBookingForm",
            courseId,
            `/umbraco/surface/StripeSurface/SaveCourseBookingFormToSession?key=${courseId}`,
            "/umbraco/surface/CourseBookingSurface/SaveCourseBookingToCrm",
            btn.getAttribute("data-success-url") ?? "",
            btn.getAttribute("data-failure-url") ?? "",
            poNumber
        );
    }

    public async initializeMembershipApplicationPayment(
        formId: string,
        contactId: string,
        applicationId: string,
        successUrl: string,
        failureUrl: string,
        total: string | number,
        item: string
    ): Promise<void> {
        await this.checkStatus();

        this.options = {
            formId: formId,
            contactId: contactId,
            applicationId: applicationId,
            successUrl: successUrl,
            failureUrl: failureUrl,
            total: total,
            item: item
        };

        const form = document.querySelector("#payment-form");
        form?.addEventListener("submit", this.handleSubmitMembershipApplication.bind(this));

        const response = await fetch(
            `/umbraco/surface/StripeSurface/GetPaymentIntent?amount=${this.options.total}` +
            `&itemName=${encodeURIComponent(this.options.item)}` +
            `&applicationId=${encodeURIComponent(this.options.applicationId ?? "")}` +
            `&userJourney=${encodeURIComponent("Membership Application")}`,
            { method: "POST" }
        );

        const { clientSecret, publicApiKey }: StripeResponse = await response.json();

        this.stripe = Stripe(publicApiKey);

        const appearance = {
            theme: "stripe"
        };

        this.elements = this.stripe.elements({ appearance, clientSecret });

        const paymentElementOptions = {
            layout: "tabs"
        };

        const paymentElement = this.elements.create("payment", paymentElementOptions);
        paymentElement.mount("#payment-element");
    }

    private async handleSubmitEventBooking(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.stripe || !this.elements) return;
        if (!this.options?.keyId) return;
        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!successUrl || !failureUrl) return;
        await this.submitEventCardPayment(formId, keyId, successUrl, failureUrl);
    }

    private async handleSubmitCourseBooking(e: Event): Promise<void> {
        e.preventDefault();
        if (!this.stripe || !this.elements) return;
        if (!this.options?.keyId) return;
        const { formId, keyId, successUrl, failureUrl } = this.options;
        if (!successUrl || !failureUrl) return;
        await this.submitCourseCardPayment(formId, keyId, successUrl, failureUrl);
    }

    private async submitEventCardPayment(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form)
        );
        if (!saved) return;

        if (!(await this.checkEventBookableBeforePayment(keyId))) return;

        const url = new URL("/umbraco/surface/EventBookingSurface/SaveEventBookingToCrm", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "card");
        await this.confirmStripePayment(url.toString());
    }

    /**
     * Asks the server whether the booking can still be placed (Bookable Until, Event Status,
     * places) before Stripe takes the money, so an event that closed or sold out after checkout
     * loaded isn't paid for and then refused (50471). Pay stays off when it can't go ahead at all.
     */
    private async checkEventBookableBeforePayment(eventId: string): Promise<boolean> {
        this.setLoading(true);
        try {
            const response = await fetch("/umbraco/surface/EventBookingSurface/CheckBookableBeforeCardPayment", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ eventId }),
            });

            if (response.status === 429) {
                this.setLoading(false);
                this.stopCardPayment("Too many attempts. Please wait a minute and try again.", false);
                return false;
            }

            const result: { success: boolean; retry?: boolean; message?: string } = await response.json();
            if (result.success) return true;

            this.setLoading(false);
            this.stopCardPayment(result.message ?? CARD_UNAVAILABLE_MESSAGE, !result.retry);
            return false;
        } catch (err) {
            console.error("[payment] CheckBookableBeforeCardPayment failed", err);
            this.setLoading(false);
            this.stopCardPayment("We couldn't check this booking just now. Please try again.", false);
            return false;
        }
    }

    private async submitCourseCardPayment(
        formId: string,
        keyId: string,
        successUrl: string,
        failureUrl: string
    ): Promise<void> {
        BookingFormValidator.setupTermsValidation(formId);
        if (!BookingFormValidator.validateBookingForm(formId)) return;

        const saved = await BookingFormValidator.saveFormToSession(
            formId,
            keyId,
            (form: HTMLFormElement) => this.getBookingFormData(form),
            `/umbraco/surface/StripeSurface/SaveCourseBookingFormToSession?key=${keyId}`
        );
        if (!saved) return;

        const url = new URL("/umbraco/surface/CourseBookingSurface/SaveCourseBookingToCrm", window.location.origin);
        url.searchParams.set("key", keyId);
        url.searchParams.set("successUrl", successUrl);
        url.searchParams.set("failureUrl", failureUrl);
        url.searchParams.set("paymentMethod", "card");
        await this.confirmStripePayment(url.toString());
    }

    private async confirmStripePayment(returnUrl: string): Promise<void> {
        this.setLoading(true);
        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: { return_url: returnUrl }
        });
        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }
        this.setLoading(false);
    }

    private async handleSubmitMembershipApplication(e: Event): Promise<void> {
        e.preventDefault();

        if (!this.options) return;

        const appForm = document.getElementById(this.options.formId) as HTMLFormElement;

        const returnUrl =
            `${window.location.href.split("/").slice(0, 3).join("/")}/umbraco/surface/MembershipApplicationSurface/ConfirmStripePayment?key=${this.options.contactId}&successUrl=${encodeURIComponent(this.options.successUrl)}&failureUrl=${encodeURIComponent(this.options.failureUrl)}`;

        // Validate form
        if (!appForm.checkValidity()) {
            appForm.reportValidity();
            return;
        } else {
            // save the form to session
            try {
                const form = document.getElementById(this.options.formId) as HTMLFormElement;
                const formData = JSON.stringify(this.getFormData(form));

                const response = await fetch("/umbraco/surface/MembershipApplicationSurface/SavePaymentStep", {
                    method: "POST",
                    body: formData,
                    headers: {
                        "Content-Type": "application/json"
                    },
                    cache: 'no-cache'
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

            } catch (err) {
                console.error(err);
                return;
            }
        }

        this.setLoading(true);

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl
            }
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === "card_error" || error.type === "validation_error") {
            this.showMessage(error.message);
        } else {
            this.showMessage("An unexpected error occurred.");
        }

        this.setLoading(false);
    }

    // Fetches the payment intent status after payment submission
    private async checkStatus(): Promise<void> {
        const clientSecret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret"
        );

        if (!clientSecret || !this.stripe) {
            return;
        }

        const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);

        switch (paymentIntent.status) {
            case "succeeded":
                this.showMessage("Payment succeeded!");
                break;
            case "processing":
                this.showMessage("Your payment is processing.");
                break;
            case "requires_payment_method":
                this.showMessage("Your payment was not successful, please try again.");
                break;
            default:
                this.showMessage("Something went wrong.");
                break;
        }
    }


    public handleSubmitVoucherCode(e: Event): void {
        e.preventDefault();
        const input = document.getElementById("ecommDiscountInput") as HTMLInputElement | null;
        if (!input) return;
        const code = input.value.trim();
        if (!code) return;

        this.submitVoucherCode(code);
    }

    private async submitVoucherCode(code: string): Promise<void> {
        const msg = document.getElementById("voucherMessage");
        if (msg) msg.style.display = "none";

        // Get formId and keyId from the current options
        const formId = this.options?.formId;
        const keyId = this.options?.keyId;

        if (!formId || !keyId) {
            if (msg) {
                msg.textContent = "Unable to save form: missing form or key.";
                msg.className = "text-danger";
                msg.style.display = "block";
            }
            return;
        }

        try {
            // Save the form to session before applying the voucher
            const saved = await BookingFormValidator.saveFormToSession(
                formId,
                keyId,
                (form: HTMLFormElement) => this.getBookingFormData(form),
                `/umbraco/surface/StripeSurface/SaveEcommerceFormToSession?key=${keyId}`
            );

            if (!saved) {
                if (msg) {
                    msg.textContent = "Failed to save form before applying voucher.";
                    msg.className = "text-danger";
                    msg.style.display = "block";
                }
                return;
            }

            // Now apply the voucher
            const response = await fetch("/umbraco/surface/ECommerceSurface/ApplyStoreDiscount", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code }),
            });

            const result = await response.json();

            if (msg) {
                msg.textContent = result.message;
                msg.className = result.success ? "text-success" : "text-danger";
                msg.style.display = "block";
            }

            if (result.success) {
                window.location.reload();
            }
        } catch {
            if (msg) {
                msg.textContent = "Something went wrong. Please try again.";
                msg.className = "text-danger";
                msg.style.display = "block";
            }
        }
    }

    public async handleRemoveVoucherCode(e: Event): Promise<void> {
        e.preventDefault();

        const formId = this.options?.formId;
        const keyId = this.options?.keyId;

        if (formId && keyId) {
            await BookingFormValidator.saveFormToSession(
                formId,
                keyId,
                (form: HTMLFormElement) => this.getBookingFormData(form),
                `/umbraco/surface/StripeSurface/SaveEcommerceFormToSession?key=${keyId}`
            );
        }

        try {
            const response = await fetch("/umbraco/surface/ECommerceSurface/RemoveStoreDiscount", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });

            const result = await response.json();

            if (result.success) {
                window.location.reload();
            }
        } catch {
            console.error("Failed to remove store discount");
        }
    }

    // ------- UI helpers -------

    

    private showMessage(messageText: string): void {
        const messageContainer = document.querySelector("#payment-message");

        if (messageContainer) {
            messageContainer.classList.remove("hidden", "d-none");
            messageContainer.textContent = messageText;

            setTimeout(() => {
                messageContainer.classList.add("hidden", "d-none");
                messageContainer.textContent = "";
            }, 4000);
        }
    }

    // Stays up, unlike showMessage's. With disablePay, Pay is off until the page is reloaded.
    private stopCardPayment(messageText: string, disablePay: boolean = true): void {
        const submitButton = document.querySelector("#submit") as HTMLButtonElement | null;
        if (submitButton && disablePay) submitButton.disabled = true;

        const messageContainer = document.querySelector("#payment-message");
        if (messageContainer) {
            messageContainer.classList.remove("hidden", "d-none");
            messageContainer.textContent = messageText;
        }
    }

    // Show a spinner on payment submission
    private setLoading(isLoading: boolean): void {
        const submitButton = document.querySelector("#submit") as HTMLButtonElement;
        const spinner = document.querySelector("#spinner");
        const buttonText = document.querySelector("#button-text") as HTMLElement | null;

        // A form whose text has a data-loading-text shows it beside the spinner, and gets its own
        // text back afterwards. The others keep their text as it was.
        const loadingText = buttonText?.dataset.loadingText;
        if (buttonText && loadingText && buttonText.dataset.idleText === undefined) {
            buttonText.dataset.idleText = buttonText.textContent ?? "";
        }

        if (isLoading) {
            // Disable the button and show a spinner
            if (submitButton) submitButton.disabled = true;
            spinner?.classList.remove("d-none");
            if (buttonText && loadingText) buttonText.textContent = loadingText;
        } else {
            if (submitButton) submitButton.disabled = false;
            spinner?.classList.add("d-none");
            if (buttonText?.dataset.idleText !== undefined) buttonText.textContent = buttonText.dataset.idleText;
        }
    }

    private getFormData(form: HTMLFormElement): { [key: string]: string } {
        const formData = new FormData(form);
        const indexedArray: { [key: string]: string } = {};

        // Use getAll to handle ASP.NET's checkbox pattern, which renders both a
        // checkbox (value="true") and a hidden field (value="false") with the same
        // name. A plain forEach would let the hidden "false" always overwrite the
        // checkbox "true". Instead, prefer "true" if it appears among the values.
        for (const key of new Set(formData.keys())) {
            const values = formData.getAll(key).map(v => v.toString());
            indexedArray[key] = values.includes('true') ? 'true' : values[values.length - 1];
        }

        return indexedArray;
    }

    private getBookingFormData(form: HTMLFormElement): Record<string, any> {
        const fd = new FormData(form);
        const data: Record<string, any> = {};

        // 1) pull raw values (strings)
        for (const [key, value] of fd.entries()) {
            // ignore files here
            if (value instanceof File) continue;

            // If you have multi-select/checkbox groups with same name, support arrays:
            if (key in data) {
                data[key] = Array.isArray(data[key]) ? [...data[key], value] : [data[key], value];
            } else {
                data[key] = value;
            }
        }

        // 2) helpers
        const toBool = (v: any) => {
            if (typeof v === "boolean") return v;
            if (typeof v === "string") return v.toLowerCase() === "true" || v === "on" || v === "1";
            if (typeof v === "number") return v === 1;
            return false;
        };

        const toNumber = (v: any) => {
            if (typeof v === "number") return v;
            if (typeof v !== "string") return v;
            const n = Number(v);
            return Number.isFinite(n) ? n : v; // if not numeric, leave as-is
        };

        // 3) coerce known numeric fields (add/remove as needed)
        const numberFields = [
            "Total",
            "SubTotal",
            "TotalVat",
            "EarlyBirdDiscountTotal",
            "DiscountTotal",
            "TotalBookings",
            "PaymentMethod",
            "ShippingCost",
            "HowDidYouHear"
        ];

        // An unanswered dropdown posts "", which the server can't read as a number.
        if (data["HowDidYouHear"] === "") delete data["HowDidYouHear"];

        for (const f of numberFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toNumber(data[f]);
            }
        }

        // 4) coerce known boolean fields (add/remove as needed)
        const boolFields = [
            "SameAsAbove",
            "InvoiceTermsAgreed",
            "PayByCard",
            "PayByInvoice",
            "ChargedToOrganisationAvailable",
            "ChargeToOrganisation",
            "TermsAllAgreed",
            "BillingAddressDetailsAreTheSame",
            "RequiresShipping",
            "RequiresShippingTelephone",
            "AgreeToTermsAndConditions",
            "HowDidYouHearAsked"
        ];

        for (const f of boolFields) {
            if (data[f] !== undefined && data[f] !== null && data[f] !== "") {
                data[f] = toBool(data[f]);
            }
        }

        return data;
    }

}

